import { useMemo, useCallback, type ComponentProps } from "react";
import { type CassetteSliderSectionList } from "@userFrontend/components/CassetteSliderSectionList";
import { useSWRData } from "@root/shared/hooks/useSWRData";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { useEntryLink } from "@userFrontend/hooks/useEntryLink";
import { type Cassette } from "./type";

type Section = {
  url: string;
  title: string;
  cassettes: Cassette[];
};

export const useCategoryTopSectionList = (
  fetchUrl: string,
): ComponentProps<typeof CassetteSliderSectionList>["sections"] | undefined => {
  const { handleClickEntryLink } = useEntryLink();

  const createData = useCallback(
    (sections: Section[]) => {
      return sections.map((section) => {
        return {
          url: section.url,
          title: section.title,
          cassettes: section.cassettes.map((item: Cassette) => ({
            type: "simplified" as const,
            gaAction: item.events.cassetteEvent.cassetteGaAction,
            gaLabel: item.events.cassetteEvent.cassetteGaLabel,
            detailUrl: item.detailUrl,
            headerContents: {
              imgSrc: item.imgSrc,
              dummyImgPath: item.dummyImgPath,
              shopOrBrandName: item.shopOrBrandName,
              businessTypeAndOccupation: item.businessTypeAndOccupation,
              title: item.title,
            },
            salaries: item.salaries,
            shopLocation: item.shopLocation,
            buttonProps: {
              workCode: item.workCode,
              hasKeeped: item.keepButtonContent.hasKeeped,
              keepButtonUrl: item.keepButtonContent.keepButtonUrl,
              keepGaAction: item.events.keepButtonEvent.keepGaAction,
              keepGaLabel: item.events.keepButtonEvent.keepGaLabel,
              entryUrl: item.entryUrl,
              onClickEntryLink: handleClickEntryLink,
            },
          })),
        };
      });
    },
    [handleClickEntryLink],
  );

  const { data: sections } = useSWRData<Section[]>({
    url: fetchUrl,
    options: { onError: (error: Error) => sendErrorLog(error) },
  });

  return useMemo(() => (sections ? createData(sections) : undefined), [sections, createData]);
};
